import { styled } from '@mui/material/styles';
import ResourcesSidebar, {
  RESOURCES_SIDEBAR_WIDTH,
} from '@/components/ResourcesSidebar/ResourcesSidebar';
import { useCallback, useMemo, useState, useRef } from 'react';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { FormattedMessage } from 'react-intl';
import { Box } from '@mui/material';
import { NavigationHeight } from '@/components/Navigation/NavigationHelpers';
import SearchTextField from '@/components/SearchTextField/SearchTextField';
import { fetchGames } from '@/lib/api';
import { mapFiltersToParams } from '../Resources/ResourcePageHelpers';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { ColumnDef } from '@/components/Table/TableHelpers';
import { GameSchema } from '@/lib/types';
import { useNavigate } from 'react-router';
import IconButton from '@/components/IconButton/IconButton';
import EditPencilIcon from '@/assets/edit-pencil.svg?react';
import UploadIcon from '@/assets/upload.svg?react';
import Button from '@/components/Button/Button';
import ComplianceDocumentsUploadModal from '@/components/ComplianceDocumentsUploadModal/ComplianceDocumentsUploadModal';
import { FileTypesComplianceGame } from '@/constants/FileTypesCompliance';
import Table from '@/components/Table/Table';
import {
  ScrollContainerContext,
  ScrollContainerRef,
} from '@/contexts/ScrollContainerContext';

const GamesAdminPage = () => {
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<FilterSchema>();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const filterParams = mapFiltersToParams(filters, undefined, {
    game_name: searchText,
  });
  const navigate = useNavigate();

  const { data, isFetchingNextPage } = useInfiniteScroll({
    fetchFn: fetchGames,
    queryKey: ['games', ...Object.values(filterParams)],
    params: filterParams,
    scrollContainerRef:
      scrollContainerRef as React.RefObject<HTMLElement>,
    pageSize: 10,
    scrollThreshold: 200,
    extractData: (response) => response.games,
  });

  const handleFiltersChange = useCallback(
    (newFilters: FilterSchema) => {
      setFilters(newFilters);
    },
    [],
  );

  const visibleSections = useMemo(
    () => ({
      market: true,
      gameStatus: true,
      volatility: true,
      gameFeatures: true,
      rtp: true,
      gameFamily: true,
      releaseDate: true,
      language: true,
    }),
    [],
  );

  const columns: ColumnDef<GameSchema>[] = [
    {
      field: 'game_name',
      label: 'Game Name',
      width: '50%',
      render: (game) => (
        <GameNameCell>
          {game?.photo_url ? (
            <GameLogo
              style={{
                backgroundImage: `url(${game?.photo_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          ) : null}
          {game?.game_name}
        </GameNameCell>
      ),
    },
    {
      id: 'edit',
      label: <FormattedMessage id="edit" defaultMessage="Edit" />,
      width: '10%',
      sortable: false,
      render: (row) => (
        <IconButton
          onClick={() => navigate(`/admin/games/${row.id}`)}
        >
          <EditPencilIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Root data-sidebar-open={isSidebarOpen}>
      <ResourcesSidebar
        onFiltersChange={handleFiltersChange}
        visibleSections={visibleSections}
        onOpenChange={setIsSidebarOpen}
        fileTypeComplianceConfig={FileTypesComplianceGame}
      />
      <ScrollContainerContext.Provider
        value={scrollContainerRef as unknown as ScrollContainerRef}
      >
        <div
          ref={scrollContainerRef}
          className="admin-page-wrapper wide-scrollbar"
        >
          <div className="admin-content page-width-wrapper">
            <Main>
              <Box
                sx={{ display: 'flex', width: '100%', marginTop: '40px' }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Title>
                    <FormattedMessage
                      id="gamesAdmin"
                      defaultMessage="Games Admin"
                    />
                  </Title>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => setUploadModalOpen(true)}
                  aria-label="Upload Compliance Documents Button"
                >
                  <UploadIcon />
                  <FormattedMessage
                    id="uploadComplianceDocuments"
                    defaultMessage="Upload Compliance Documents"
                  />
                </Button>
              </Box>
              <StyledSearchTextField
                placeholder={
                  <FormattedMessage
                    id="filterAsYouType"
                    defaultMessage="Filter as you type"
                  />
                }
                value={searchText}
                onChange={setSearchText}
              />
              <StyledTable rows={data} columns={columns} />
              {isFetchingNextPage && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <FormattedMessage
                    id="loading"
                    defaultMessage="Loading..."
                  />
                </Box>
              )}
            </Main>
          </div>
        </div>
      </ScrollContainerContext.Provider>
      <ComplianceDocumentsUploadModal
        open={uploadModalOpen}
        initialShowOperator
        initialShowMarkets
        onClose={() => setUploadModalOpen(false)}
        onSave={() => setUploadModalOpen(false)}
      />
    </Root>
  );
};

const Root = styled('div')({
  position: 'relative',
  width: '100%',
  background: '#F9FAFB',
  '& .admin-page-wrapper': {
    height: `calc(100vh - ${NavigationHeight}px)`,
    overflowY: 'auto',
    width: '100%',
  },
  '& .admin-content': {
    width: '100%',
    transition: 'padding 0.25s',
    paddingLeft: 0,
  },
  '&[data-sidebar-open="true"] .admin-content': {
    paddingLeft: RESOURCES_SIDEBAR_WIDTH,
  },
});

const Main = styled('div')(({ theme }) => ({
  width: '100%',
  padding: `0 ${theme.spacing(4)}`,
}));

const Title = styled('div')(({ theme }) => ({
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  marginBottom: 8,
}));

const StyledSearchTextField = styled(SearchTextField)({
  maxWidth: 500,
});

const StyledTable = styled(Table<GameSchema>)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const GameNameCell = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 8,
});

const GameLogo = styled('div')(({ theme }) => ({
  flexShrink: 0,
  width: 50,
  height: 50,
  background: '#fff',
  borderRadius: theme.radii.xxs,
  overflow: 'hidden',
}));

export default GamesAdminPage;
